// Typed wrappers for the new ms-project /keys, /me, /keys/.../scopes
// endpoints. These exist because the @microtica/ms-project-sdk package
// hasn't been republished yet with the matching swagger entries — once
// it has, these can be replaced with calls on `getProjectService()`.
//
// We deliberately reuse the same `axiosInstance` (and therefore the
// same auth interceptor) as the rest of the backend layer so the
// behavior matches exactly.
import axios, { AxiosInstance } from "axios";
import { registerRequestInterceptor } from "./interceptors";
import { env } from "../env";
import { getIdToken } from "./index";

/**
 * Feature-flag gate for the API Keys page. Mirrors the
 * REACT_APP_PREVIEW_PLAYGROUND_PROJECTS pattern: comma-separated list of
 * project ids, or `*` to enable for everyone. Empty / missing => off.
 */
export function isApiKeysEnabled(projectId: string): boolean {
    const list = (env.REACT_APP_API_KEYS_PROJECTS || "")
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
    return list.includes("*") || list.includes(projectId);
}

const axiosInstance: AxiosInstance = axios.create();
// Cast for the interceptor registration — the existing backend layer does
// the same dance because the interceptor module accepts a loose shape.
// tslint:disable-next-line: no-any
registerRequestInterceptor(axiosInstance as any);

function baseUrl(): string {
    return `${env.REACT_APP_BACKEND_ENDPOINT}/ms-project`;
}

function authHeaders(): Record<string, string> {
    const token = getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export type Permission = "read" | "write" | "admin" | "owner";
export type EntityKind =
    | "project"
    | "stage"
    | "pipeline"
    | "resource"
    | "kubernetes"
    | "component"
    | "awsaccount"
    | "microservice"
    | "gitaccount"
    | "azureaccount"
    | "gcpaccount";

export interface ApiKeyMeta {
    keyId: string;
    keyPrefix: string;
    ownerUserId: string;
    name: string;
    createdAt: number;
    expiresAt?: number | null;
    revokedAt?: number | null;
    lastUsedAt?: number | null;
    createdBy: string;
}

export interface CreateApiKeyResponse {
    keyId: string;
    keyPrefix: string;
    /** Full mic_<prefix>_<secret> token. Show ONCE; never recoverable. */
    plaintext: string;
}

export interface ApiKeyScope {
    assigneeId: string;
    assigneeType: "apikey";
    entityId: string;
    entityType: EntityKind;
    permission: Permission;
}

export interface MeResponse {
    via: "user" | "apikey";
    userId: string;
    apiKey: { id: string; prefix?: string; name?: string } | null;
}

export async function listApiKeys(): Promise<ApiKeyMeta[]> {
    const res = await axiosInstance.get<{ keys: ApiKeyMeta[] }>(
        `${baseUrl()}/keys`,
        { headers: authHeaders() },
    );
    return res.data.keys;
}

export async function createApiKey(input: {
    name: string;
    expiresAt?: number;
}): Promise<CreateApiKeyResponse> {
    const res = await axiosInstance.post<CreateApiKeyResponse>(
        `${baseUrl()}/keys`,
        input,
        { headers: authHeaders() },
    );
    return res.data;
}

export async function revokeApiKey(keyId: string): Promise<void> {
    await axiosInstance.delete(`${baseUrl()}/keys/${keyId}`, {
        headers: authHeaders(),
    });
}

export async function listApiKeyScopes(keyId: string): Promise<ApiKeyScope[]> {
    const res = await axiosInstance.get<{ scopes: ApiKeyScope[] }>(
        `${baseUrl()}/keys/${keyId}/scopes`,
        { headers: authHeaders() },
    );
    return res.data.scopes;
}

export async function grantApiKeyScope(
    keyId: string,
    input: {
        entityId: string;
        entityType: EntityKind;
        permission: Permission;
        /**
         * Project that owns the entity. Required by the backend's
         * privilege-escalation guard so it can run the same OR-
         * resolution as the runtime auth check (direct row, or
         * project-level fallback).
         */
        projectId: string;
    },
): Promise<void> {
    await axiosInstance.post(
        `${baseUrl()}/keys/${keyId}/scopes`,
        input,
        { headers: authHeaders() },
    );
}

export async function revokeApiKeyScope(
    keyId: string,
    entityId: string,
): Promise<void> {
    await axiosInstance.delete(
        `${baseUrl()}/keys/${keyId}/scopes/${encodeURIComponent(entityId)}`,
        { headers: authHeaders() },
    );
}

export async function getMe(): Promise<MeResponse> {
    const res = await axiosInstance.get<MeResponse>(`${baseUrl()}/me`, {
        headers: authHeaders(),
    });
    return res.data;
}
